import { mapGetters } from 'vuex';
import { searchLocalNumbersApi, searchTollFreeNumbersApi, validatePortNumbers } from '@/apis/pbx';
import { get, includes, isString, omitBy } from 'lodash';
import { isEmptyVal } from '@/utils';
import { mapBackendPhoneNumberList, transferSearchToVanityNumber } from '@/plans/ZOOM_PHONE/helper/utils';
import {
  defaultTollFreePrefix,
  PBX_REGIONS,
  PBX_REGIONS_MAP_COUNTRY,
  phoneNumberTypeMap,
} from '@/plans/ZOOM_PHONE/helper/constants';

const ipLocationSign = 1;
const defaultCountryState = 'CA';

const omitEmptyVal = (value) => omitBy(value, isEmptyVal);

export default {
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    ...mapGetters(['ipLocation']),
    currentIpGeoCountryCode() {
      return get(this.ipLocation, 'cn');
    },
    ipIsAvailable() {
      return includes(PBX_REGIONS_MAP_COUNTRY.US, this.currentIpGeoCountryCode);
    },
    realIpGeoCountryCode() {
      return this.ipIsAvailable ? this.currentIpGeoCountryCode : PBX_REGIONS.US_AND_CANADA;
    },
  },
  methods: {
    getLocalNumberParamsWithIp() {
      const countryIsoCode = this.realIpGeoCountryCode;

      // First, it is automatically located and obtained according to the ip,
      // otherwise it is California according to the current state
      return this.ipIsAvailable ? {
        countryIsoCode,
        ipSign: ipLocationSign,
      } : {
        countryIsoCode,
        state: defaultCountryState,
      };
    },
    getTollFreeNumberParamsWithIp() {
      // By default, it is based on the number prefix of 888 and country is US&CA search
      return {
        selectTfPrefix: defaultTollFreePrefix,
        countryIsoCode: PBX_REGIONS.US_AND_CANADA,
      };
    },
    async searchLocalNumbers(formParams = {}) {
      try {
        this.toggleLoading(true);
        const { ipSign, countryIsoCode, limit = 100, city, state, areaCode } = formParams;
        const params = omitEmptyVal({ ipSign, countryIsoCode, limit, city, state, areaCode });

        const { result } = await searchLocalNumbersApi(params);
        const exception = isString(result) || isEmptyVal(result);
        return exception ? [] : mapBackendPhoneNumberList(result, {
          type: phoneNumberTypeMap.local,
          search: areaCode,
          ...params,
        });
      } catch (e) {
        return [];
      } finally {
        this.toggleLoading(false);
      }
    },
    async searchTollFreeNumbers(formParams = {}) {
      try {
        this.toggleLoading(true);
        const { vanitySuffix, selectTfPrefix, countryIsoCode, limit = 100 } = formParams;
        const isVanity = isEmptyVal(vanitySuffix) === false;
        const params = omitEmptyVal({
          limit,
          countryIsoCode,
        });

        if (isVanity) {
          params.matching = transferSearchToVanityNumber(vanitySuffix);
        } else if (selectTfPrefix) {
          params.tollFreeWildCardPattern = selectTfPrefix;
        }

        const { result } = await searchTollFreeNumbersApi(params);
        const exception = isString(result) || isEmptyVal(result);
        return exception ? [] : mapBackendPhoneNumberList(result, {
          search: vanitySuffix,
          isVanity,
          type: phoneNumberTypeMap.tollFree,
        });
      } catch (e) {
        return [];
      } finally {
        this.toggleLoading(false);
      }
    },
    toggleLoading(value = false) {
      this.loading = value;
      this.$emit('toggleLoading', value);
    },
    async validatePortNumbers(portNumber) {
      try {
        this.toggleLoading(true);
        const { result: validResult } = await validatePortNumbers([portNumber]);
        const { result, errorCode } = validResult || {};

        if (!result || errorCode !== 0 || result.failed?.length) {
          throw new Error('verify exception.');
        }

        return '';
      } catch (e) {
        return this.$t('billing.pbx.port_number.not_valid');
      } finally {
        this.toggleLoading(false);
      }
    },
  },
};
